import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Modal,
  IconButton,
  TextField,
} from '@material-ui/core';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import OTPInput from 'react-otp-input';
import Numpad from 'components/authentication/login/Numpad';
import FourDigitDisplay from 'components/authentication/login/FourDigitDisplay';
import useUser from 'utils/api/users';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export default function AccountChangeMPIN({ open, onClose, user, phone }) {
  const [MPINStep, setMPINStep] = useState('email');
  const [MPIN, setMPIN] = useState('');
  const [confirmMPIN, setConfirmMPIN] = useState('');
  const [email, setEmail] = useState('');
  const [showMPIN, setShowMPIN] = useState(false);
  const [otp, setOtp] = useState('');
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(null);

  // Handle countdown timer for OTP resend
  React.useEffect(() => {
    if (countdown > 0) {
      intervalRef.current = setInterval(() => {
        setCountdown((prevCountdown) => {
          if (prevCountdown <= 1) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            return 0;
          }
          return prevCountdown - 1;
        });
      }, 1000);
      return () => clearInterval(intervalRef.current);
    }
  }, [countdown]);

  const handleMPINChange = (value) => {
    switch (MPINStep) {
      case 'input':
        setMPIN(value);
        break;
      case 'confirm':
        setConfirmMPIN(value);
        break;
      default:
        console.log('Unknown operation type');
        break;
    }
  };

  const handleMPINClear = () => {
    switch (MPINStep) {
      case 'input':
        setMPIN("");
        break;
      case 'confirm':
        setConfirmMPIN("");
        break;
      default:
        console.log('Unknown operation type');
        break;
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendMPINChangeOtp = async () => {
    setCountdown(10);
    try {
        let data
        if (phone) {
             data = {
                phone: phone,
                email: email
            }
        } else {
             data = {
                phone: user.phone,
                email: email
            }
        }
      if (countdown === 0) {
        await useUser.send_MPIN_change_otp(data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleMPINEnter = async () => {
    let data
    switch (MPINStep) {
      case 'email':
        if (!email || email.trim() === '') {
          alert('Please enter your email address');
          return;
        }
        if (!validateEmail(email)) {
          alert('Please enter a valid email address');
          return;
        }
        setMPINStep('input');
        break;
      case 'input':
        if (!MPIN || MPIN.length < 4) {
          alert('Please enter a 4-digit MPIN');
          return;
        }
        setMPINStep('confirm');
        break;
      case 'confirm':
        if (!confirmMPIN || confirmMPIN.length < 4) {
          alert('Please enter a 4-digit MPIN');
          return;
        }
        if (MPIN !== confirmMPIN) {
          alert('MPINs do not match');
          break;
        }
        handleSendMPINChangeOtp();
        setMPINStep('otp');
        break;
      case 'otp':
        try {
          if (otp === '') {
            alert("Please enter OTP");
            return;
          }
          if (MPIN === "") {
            alert("Please enter MPIN");
            return;
          }
          
          if (MPIN.length < 4) {
            alert("MPIN must be at least 4 digits");
            return;
          }
          if(phone) {   
            data = {
                phone: phone,
                mpin: MPIN,
                otp: otp
            }
          } else {
            data = {
                id: user._id,
                mpin: MPIN,
                otp: otp,
                phone: user.phone
            };
          }
          const result = await useUser.set_MPIN(data);
          if (!result.data.success) {
            alert('Failed to set MPIN');
            return;
          }
          alert('MPIN set successfully');
          handleClose();
        } catch (error) {
          console.error('An error occurred:', error);
          alert('Failed to set MPIN');
        }
        break;
      default:
        console.log('Unknown operation type');
        break;
    }
  };

  const handleClose = () => {
    setMPIN("");
    setConfirmMPIN("");
    setEmail("");
    setMPINStep('email');
    setOtp("");
    onClose();
  };

  const getStepTitle = () => {
    switch (MPINStep) {
      case 'email':
        return 'Enter Your Email';
      case 'input':
        return 'Set New MPIN';
      case 'confirm':
        return 'Confirm MPIN';
      case 'otp':
        return 'Enter OTP';
      default:
        return 'Set New MPIN';
    }
  };

  const getNextButtonText = () => {
    switch (MPINStep) {
      case 'email':
        return 'Next';
      case 'input':
        return 'Next';
      case 'confirm':
        return 'Send OTP';
      case 'otp':
        return 'Submit';
      default:
        return 'Next';
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <Typography variant="h6" component="h2">
          {getStepTitle()}
        </Typography>

        {MPINStep === 'email' && (
          <>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              Please enter your email address to receive the OTP for MPIN change.
            </Typography>
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              variant="outlined"
              sx={{ mt: 2, mb: 2 }}
              placeholder="Enter your email address"
              required
            />
          </>
        )}

        {MPINStep === 'otp' ? (
          <>
            <Typography variant="body2" sx={{ mt: 1, mb: 2 }}>
              Please enter the OTP sent to {email}.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, width: "100%" }}>
              <OTPInput
                value={otp}
                onChange={setOtp}
                numInputs={6}
                inputType="number"
                shouldAutoFocus
                containerStyle={{ display: "flex", gap: "8px" }}
                inputStyle={{
                  width: "3rem",
                  height: "3rem",
                  fontSize: "1.5rem",
                  borderRadius: "8px",
                  border: "1px solid #ccc",
                  textAlign: "center",
                  outline: "none",
                  background: "#fff",
                  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                  transition: "border-color 0.2s",
                }}
                renderInput={(props) => <input {...props} />}
              />
            </Box>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
              {countdown > 0 ? (
                <Typography variant="body2" color="error">
                  Please wait {countdown} seconds before sending another OTP
                </Typography>
              ) : (
                <Button variant="contained" color="primary" onClick={handleSendMPINChangeOtp}>
                  Resend OTP
                </Button>
              )}
            </Box>
          </>
        ) : MPINStep !== 'email' && (
          <>
            <Box mt={4} display={'flex'} width={'100%'} sx={{mb: 2}}>
              <FourDigitDisplay value={MPINStep === 'confirm' ? confirmMPIN : MPIN} type={showMPIN ? 'set' : 'enter'} sx={{flexGrow: 1}}/>
            </Box>
            <Box display={'flex'} justifyContent={'center'} width={'100%'} sx={{mb: 1}}>
              <IconButton onClick={() => setShowMPIN(prev => !prev)} >
                { showMPIN ? <VisibilityOff /> : <Visibility /> }
              </IconButton>
            </Box>
            
            <Numpad
              onChange={(value) => handleMPINChange(value)}
              onClear={handleMPINClear}
              initialValue={MPINStep === 'confirm' ? confirmMPIN : MPIN}
            />
          </>
        )}
        
        <Box mt={2} display="flex" width={'100%'} justifyContent="space-between">
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleMPINEnter}>
            {getNextButtonText()}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
